import { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { Column, flexRender, Header, Table } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import Filter from "./Filter";

interface HeaderProps<T extends Record<string, any>> {
  table: Table<T>;
  header: Header<T, unknown>;
}

const THead = <T extends Record<string, any>>({
  table,
  header,
}: HeaderProps<T>) => {
  const getCommonPinningStyles = (column: Column<T>): CSSProperties => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px lightgray inset"
        : isFirstRightPinnedColumn
        ? "4px 0 4px -4px lightgray inset"
        : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      position: isPinned ? "sticky" : "relative",
      zIndex: isPinned ? 1 : 0,
    };
  };
  return (
    <TableHead
      className={cn(
        "relative text-left select-none min-h-12 text-sm group/head",
        {
          "cursor-pointer": header.column.getCanSort(),
          "flex-1": table.getAllColumns().length - 1 === header.index,
        }
      )}
      style={{
        width: header.getSize(),
        ...getCommonPinningStyles(header.column),
      }}
    >
      <div
        className="flex max-sm:flex-col md:gap-3 sm:items-center sm:justify-between whitespace-nowrap relative w-full px-1"
        onClick={
          header.column.getCanSort()
            ? header.column.getToggleSortingHandler()
            : undefined
        }
        role="button"
        tabIndex={0}
      >
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {(header.column.getCanFilter() || header.column.getCanSort()) && (
          <div className="flex items-center gap-1 md:pr-5 last:pr-0">
            {header.column.getCanFilter() ? (
              <Filter column={header.column} table={table} />
            ) : null}
            {header.column.getCanSort() && (
              <div className="flex flex-col text-zinc-800">
                <ChevronDown
                  className={cn(
                    "translate-y-2 translate-x-1 size-5 text-gray-800",
                    {
                      "text-green-600": header.column.getIsSorted() === "asc",
                    }
                  )}
                />
                <ChevronUp
                  className={cn(
                    "-translate-y-2 -translate-x-1 size-5 text-gray-800",
                    {
                      "text-green-600": header.column.getIsSorted() === "desc",
                    }
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {header.column.getCanResize() &&
        header.index !== table.getAllColumns().length - 1 && (
          <div
            aria-hidden="true"
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={cn(
              "absolute right-0 top-0 flex justify-end h-full w-2 cursor-col-resize select-none touch-none "
            )}
          >
            <div
              className={cn(
                "w-[1px] h-full bg-transparent peer-hover:bg-gray-200 group-hover/head:bg-gray-200 transition-all",
                {
                  "bg-gray-200 hover:bg-gray-200":
                    header.column.getIsResizing(),
                }
              )}
            />
          </div>
        )}
    </TableHead>
  );
};

export default THead;
